/**
 * AI Context Preview Modal
 * Issue #368: Show the AI prompt context for debugging and transparency
 * Issue #386: Add analysis depth selector and prompt customization
 *
 * Displays the context being sent to AI for naming an interaction,
 * including handler function, trigger, state changes, API calls, etc.
 */

import { useState, useEffect } from 'react';
import type { NamingContext } from '../../hooks/useInteractionNaming';

// Analysis depth type (Issue #386)
type AnalysisDepth = 'light' | 'standard' | 'deep';

interface AIContextPreviewModalProps {
  projectId: string;
  interactionId: string;
  interactionName: string;
  context: NamingContext;
  onClose: () => void;
  onGenerateName?: (context: NamingContext) => Promise<void>;
}

// Depth button component
function DepthButton({
  active,
  onClick,
  icon,
  label,
  description,
  color,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  description: string;
  color: 'blue' | 'slate' | 'orange';
}) {
  const colorClasses = {
    blue: active
      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300'
      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-blue-300',
    slate: active
      ? 'bg-slate-200 dark:bg-slate-700 border-slate-500 text-slate-700 dark:text-slate-200'
      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400',
    orange: active
      ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-500 text-orange-700 dark:text-orange-300'
      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-orange-300',
  };

  return (
    <button
      onClick={onClick}
      className={`flex-1 p-2 rounded-lg border-2 transition-colors ${colorClasses[color]}`}
    >
      <div className="text-lg">{icon}</div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs opacity-75">{description}</div>
    </button>
  );
}

export function AIContextPreviewModal({
  projectId,
  interactionId,
  interactionName,
  context,
  onClose,
  onGenerateName,
}: AIContextPreviewModalProps) {
  const [editableContext, setEditableContext] = useState<NamingContext>(context);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [guidelines, setGuidelines] = useState<string>('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'context' | 'customize' | 'preview'>('context');

  // Issue #386: Per-interaction prompt override state
  const [analysisDepth, setAnalysisDepth] = useState<AnalysisDepth>('standard');
  const [customGuidelines, setCustomGuidelines] = useState<string>('');
  const [hasOverride, setHasOverride] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Load custom prompts for this project
  useEffect(() => {
    async function loadPrompts() {
      setIsLoadingPrompt(true);
      try {
        const response = await fetch(`/api/projects/${projectId}/ai-naming-prompts`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setSystemPrompt(data.systemPrompt);
          setGuidelines(data.guidelines || '');
        }
      } catch (error) {
        console.error('Failed to load prompts:', error);
      } finally {
        setIsLoadingPrompt(false);
      }
    }
    loadPrompts();
  }, [projectId]);

  // Issue #386: Load per-interaction prompt override
  useEffect(() => {
    async function loadOverride() {
      try {
        const response = await fetch(
          `/api/projects/${projectId}/interactions/${interactionId}/prompt-override`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.hasOverride) {
            setAnalysisDepth(data.analysisDepth as AnalysisDepth);
            setCustomGuidelines(data.customGuidelines || '');
            setHasOverride(true);
          }
        }
      } catch (error) {
        console.error('Failed to load prompt override:', error);
      }
    }
    loadOverride();
  }, [projectId, interactionId]);

  // Update editable context when depth changes
  useEffect(() => {
    setEditableContext(prev => ({
      ...prev,
      analysisDepth,
      customGuidelines: customGuidelines || undefined,
      useDeepAnalysis: analysisDepth === 'deep',
      readFullFile: analysisDepth === 'deep',
    }));
  }, [analysisDepth, customGuidelines]);

  // Build the full prompt preview based on depth
  const buildPromptPreview = (): string => {
    // Merge guidelines if custom ones are provided
    let effectiveGuidelines = guidelines;
    if (customGuidelines) {
      effectiveGuidelines = `${guidelines}\n\n## Additional Guidelines (per-interaction):\n${customGuidelines}`;
    }

    let prompt = `${systemPrompt}\n\n## Guidelines\n${effectiveGuidelines}`;

    // Light: minimal context
    prompt += `\n\n## Interaction to name:
- Handler function: ${editableContext.handlerFunction}
- Trigger element: ${editableContext.triggerElement} (${editableContext.triggerType})`;

    if (editableContext.elementText) {
      prompt += `\n- **Element text: "${editableContext.elementText}"** (use this for naming!)`;
    }

    // Standard and Deep: add more context
    if (analysisDepth !== 'light') {
      prompt += `\n- Component file: ${editableContext.componentFile}`;

      if (editableContext.screenName) {
        prompt += `\n- Screen/Page: ${editableContext.screenName}`;
      }

      if (editableContext.interactionCategory) {
        prompt += `\n- Interaction type: ${editableContext.interactionCategory}`;
      }

      if (editableContext.flowDescription) {
        prompt += `\n- Description: ${editableContext.flowDescription}`;
      }

      if (editableContext.flowSteps && editableContext.flowSteps.length > 0) {
        const steps = editableContext.flowSteps.slice(0, 5);
        prompt += `\n- What happens: ${steps.join(' -> ')}${editableContext.flowSteps.length > 5 ? ' -> ...' : ''}`;
      }

      if (editableContext.stateChanges && editableContext.stateChanges.length > 0) {
        prompt += `\n- State changes: ${editableContext.stateChanges.join(', ')}`;
      }

      if (editableContext.apiCalls && editableContext.apiCalls.length > 0) {
        prompt += `\n- API calls: ${editableContext.apiCalls.join(', ')}`;
      }

      if (editableContext.touchedTables && editableContext.touchedTables.length > 0) {
        prompt += `\n- Database tables: ${editableContext.touchedTables.join(', ')}`;
      }

      if (editableContext.touchedServices && editableContext.touchedServices.length > 0) {
        prompt += `\n- External services: ${editableContext.touchedServices.join(', ')}`;
      }

      if (editableContext.isCompleteFlow !== undefined) {
        prompt += `\n- Flow type: ${editableContext.isCompleteFlow ? 'Complete E2E (UI -> API -> DB)' : 'UI-only interaction'}`;
      }

      if (editableContext.codeSnippet) {
        const truncated = editableContext.codeSnippet.substring(0, 500);
        prompt += `\n- Handler code:\n\`\`\`\n${truncated}${editableContext.codeSnippet.length > 500 ? '\n... (truncated)' : ''}\n\`\`\``;
      }
    }

    // Deep: note about full file
    if (analysisDepth === 'deep') {
      prompt += `\n\n## Full Source File (Deep Analysis Mode):\n[Full file content will be included server-side]`;
    }

    prompt += `\n\nGenerate a single concise name for this interaction. Respond with ONLY a JSON object:
{
  "name": "the suggested name",
  "confidence": 0.0 to 1.0,
  "reasoning": "brief explanation of why this name was chosen"
}`;

    return prompt;
  };

  const handleGenerate = async () => {
    if (onGenerateName) {
      setIsGenerating(true);
      try {
        await onGenerateName(editableContext);
        onClose();
      } finally {
        setIsGenerating(false);
      }
    }
  };

  // Issue #386: Save prompt override
  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const response = await fetch(
        `/api/projects/${projectId}/interactions/${interactionId}/prompt-override`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            analysisDepth,
            customGuidelines: customGuidelines || null,
          }),
        }
      );

      if (response.ok) {
        setHasOverride(true);
        setSaveMessage('Settings saved');
        setTimeout(() => setSaveMessage(null), 2000);
      } else {
        setSaveMessage('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save prompt override:', error);
      setSaveMessage('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // Issue #386: Reset to defaults
  const handleResetSettings = async () => {
    try {
      await fetch(
        `/api/projects/${projectId}/interactions/${interactionId}/prompt-override`,
        { method: 'DELETE', credentials: 'include' }
      );
      setAnalysisDepth('standard');
      setCustomGuidelines('');
      setHasOverride(false);
      setSaveMessage('Reset to defaults');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (error) {
      console.error('Failed to reset prompt override:', error);
    }
  };

  const updateContextField = <K extends keyof NamingContext>(
    field: K,
    value: NamingContext[K]
  ) => {
    setEditableContext(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔍</span>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                AI Naming Context
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {interactionName}
                {hasOverride && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded">
                    Custom
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setActiveTab('context')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'context'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Context Fields
          </button>
          <button
            onClick={() => setActiveTab('customize')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'customize'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Prompt Settings
            {hasOverride && <span className="ml-1 text-indigo-500">•</span>}
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'preview'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Full Prompt Preview
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === 'context' && (
            <div className="space-y-4">
              {/* Issue #386: Analysis Depth Selector */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Analysis Depth
                </h3>
                <div className="flex gap-2">
                  <DepthButton
                    active={analysisDepth === 'light'}
                    onClick={() => setAnalysisDepth('light')}
                    icon="⚡"
                    label="Light"
                    description="~200 tokens"
                    color="blue"
                  />
                  <DepthButton
                    active={analysisDepth === 'standard'}
                    onClick={() => setAnalysisDepth('standard')}
                    icon="📋"
                    label="Standard"
                    description="~800 tokens"
                    color="slate"
                  />
                  <DepthButton
                    active={analysisDepth === 'deep'}
                    onClick={() => setAnalysisDepth('deep')}
                    icon="🔬"
                    label="Deep"
                    description="~4000 tokens"
                    color="orange"
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {analysisDepth === 'light' && 'Handler + trigger only. Fastest and cheapest.'}
                  {analysisDepth === 'standard' && 'Includes component file, code snippet, API calls, tables.'}
                  {analysisDepth === 'deep' && 'Full file analysis. Best accuracy, higher cost.'}
                </p>
              </div>

              {/* Required fields */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Required Context
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Handler Function
                    </label>
                    <input
                      type="text"
                      value={editableContext.handlerFunction}
                      onChange={(e) => updateContextField('handlerFunction', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Trigger Element
                    </label>
                    <input
                      type="text"
                      value={editableContext.triggerElement}
                      onChange={(e) => updateContextField('triggerElement', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Trigger Type
                    </label>
                    <input
                      type="text"
                      value={editableContext.triggerType}
                      onChange={(e) => updateContextField('triggerType', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Component File
                    </label>
                    <input
                      type="text"
                      value={editableContext.componentFile}
                      onChange={(e) => updateContextField('componentFile', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                </div>
              </div>

              {/* Enhanced context fields - show for standard/deep only */}
              {analysisDepth !== 'light' && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Enhanced Context
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                        Screen/Page Name
                      </label>
                      <input
                        type="text"
                        value={editableContext.screenName || ''}
                        onChange={(e) => updateContextField('screenName', e.target.value || undefined)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                        placeholder="e.g., Dashboard"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                        Interaction Category
                      </label>
                      <input
                        type="text"
                        value={editableContext.interactionCategory || ''}
                        onChange={(e) => updateContextField('interactionCategory', e.target.value || undefined)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                        placeholder="e.g., toggle, navigation"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Flow Description
                    </label>
                    <textarea
                      value={editableContext.flowDescription || ''}
                      onChange={(e) => updateContextField('flowDescription', e.target.value || undefined)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 h-16"
                      placeholder="Describe what this interaction does..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                        API Calls (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={editableContext.apiCalls?.join(', ') || ''}
                        onChange={(e) => {
                          const calls = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                          updateContextField('apiCalls', calls.length > 0 ? calls : undefined);
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                        placeholder="e.g., POST /api/users"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                        Database Tables (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={editableContext.touchedTables?.join(', ') || ''}
                        onChange={(e) => {
                          const tables = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                          updateContextField('touchedTables', tables.length > 0 ? tables : undefined);
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                        placeholder="e.g., users, sessions"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Code Snippet
                    </label>
                    <textarea
                      value={editableContext.codeSnippet || ''}
                      onChange={(e) => updateContextField('codeSnippet', e.target.value || undefined)}
                      className="w-full px-2 py-1.5 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 h-24"
                      placeholder="Paste the handler code here..."
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isCompleteFlow"
                      checked={editableContext.isCompleteFlow || false}
                      onChange={(e) => updateContextField('isCompleteFlow', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
                    />
                    <label htmlFor="isCompleteFlow" className="text-sm text-slate-700 dark:text-slate-300">
                      This is a complete E2E flow (UI -&gt; API -&gt; DB)
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Issue #386: Prompt Settings Tab */}
          {activeTab === 'customize' && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Project Defaults (read-only)
                </h4>
                {isLoadingPrompt ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Loading...
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-slate-500 dark:text-slate-400">System Prompt</label>
                      <div className="mt-1 p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400 max-h-24 overflow-auto">
                        {systemPrompt.substring(0, 200)}...
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 dark:text-slate-400">Guidelines</label>
                      <div className="mt-1 p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400 max-h-20 overflow-auto whitespace-pre-wrap">
                        {guidelines}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Additional Guidelines (for this interaction)
                </label>
                <textarea
                  value={customGuidelines}
                  onChange={(e) => setCustomGuidelines(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 h-32"
                  placeholder="Add specific guidelines for naming this interaction...&#10;&#10;e.g., 'Keep names very short' or 'Focus on the modal action'"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  These guidelines will be merged with the project defaults.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-sm bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                {hasOverride && (
                  <button
                    onClick={handleResetSettings}
                    className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                  >
                    Reset to Defaults
                  </button>
                )}
                {saveMessage && (
                  <span className="text-sm text-green-600 dark:text-green-400">{saveMessage}</span>
                )}
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="space-y-4">
              {isLoadingPrompt ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="w-6 h-6 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        This is the exact prompt that will be sent to the AI:
                      </p>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        analysisDepth === 'light' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                        analysisDepth === 'deep' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                        'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {analysisDepth === 'light' && '⚡ Light'}
                        {analysisDepth === 'standard' && '📋 Standard'}
                        {analysisDepth === 'deep' && '🔬 Deep'}
                      </span>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(buildPromptPreview())}
                      className="text-xs text-indigo-500 hover:text-indigo-600 dark:text-indigo-400"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                  {analysisDepth === 'deep' && (
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 text-xs text-orange-600 dark:text-orange-400 flex items-center gap-2">
                      <span>🔬</span>
                      <span>Deep analysis enabled - full file will be included in prompt (server-side)</span>
                    </div>
                  )}
                  {customGuidelines && (
                    <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                      <span>✨</span>
                      <span>Custom guidelines will be merged with project defaults</span>
                    </div>
                  )}
                  <pre className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap overflow-auto max-h-96 font-mono">
                    {buildPromptPreview()}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {analysisDepth === 'light' && '⚡ Light analysis - minimal context'}
            {analysisDepth === 'standard' && '📋 Standard analysis - balanced context'}
            {analysisDepth === 'deep' && '🔬 Deep analysis - full file context'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            >
              Cancel
            </button>
            {onGenerateName && (
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>Generate Name</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIContextPreviewModal;
